import { LocalMediaRef, MediaRef } from "@/app/__to_be_integrated/_____components/image/enhanced-image/types/types";
import { UploadFile } from "@/app/lib/generated/graphql-types";
import DefaultImageConverter, { ImageConverter } from "@/app/lib/common/image-uploader/image-converter";
import { getUploadFileId } from "@/app/lib/common/get-image-id";
import { isLocalMediaRef, isUploadFile } from "@/app/lib/common/image-uploader/image-descriptor";
import { mediaRefType } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";

export type ImageMetadata = {
    name?: string | null;
    caption?: string | null;
    alt?: string | null;
}

export interface MediaUploader {
    upload(media: MediaRef): Promise<UploadFile & { id: string }>;
}

export class StrapiMediaManagerError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'StrapiMediaManagerError';
    }
}

export class NoFileProvidedError extends StrapiMediaManagerError {
    constructor() {
        super('No uploadfile was provided to custom-file');
        this.name = 'NoFileProvidedError';
    }
}

export class StrapiMediaManager implements MediaUploader {
    private readonly imageConverter: ImageConverter = new DefaultImageConverter();

    async upload(media: MediaRef): Promise<UploadFile & { id: string }> {
        if (isUploadFile(media)) {
            return media as UploadFile & { id: string };
        } else if (isLocalMediaRef(media)) {
            if (!media.file) {
                throw new NoFileProvidedError();
            }

            try {
                let finalUploadFile = media.file;
                if (mediaRefType(media) == 'image') {
                    finalUploadFile = await this.imageConverter.convertAndCompress(media.file);
                }

                const uploadFile = await this.uploadImageSelection({
                    ...media,
                    file: finalUploadFile
                });

                const id = await getUploadFileId(uploadFile);

                return {
                    id,
                    ...uploadFile,
                }
            } catch (error: unknown) {
                console.error('Error uploading image:', error);
                if (error instanceof Error) {
                    throw new StrapiMediaManagerError(error.message);
                } else {
                    throw new StrapiMediaManagerError('Unknown error occurred: ' + error);
                }
            }
        } else {
            throw new StrapiMediaManagerError('Invalid image reference provided. Got: ' + media);
        }
    }

    async delete(uploadFile: UploadFile) {
        const id = await getUploadFileId(uploadFile);
        const result = await fetch(`/api/upload/strapi/upload/${id}`, {
            method: 'DELETE',
        });

        return result;
    }

    async updateMetadata(uploadFile: UploadFile, metadata: ImageMetadata) {
        const id = await getUploadFileId(uploadFile);

        const formData = new FormData();
        if (metadata.name) formData.append('name', metadata.name);
        if (metadata.caption) formData.append('caption', metadata.caption);
        if (metadata.alt) formData.append('alt', metadata.alt);

        try {
            return await fetch(`/api/upload/strapi/update-metadata/${id}`, {
                method: 'POST',
                body: formData,
            });
        } catch (error) {
            if (error instanceof Error) {
                throw new StrapiMediaManagerError(error.message);
            } else {
                throw new StrapiMediaManagerError('Unknown error occurred: ' + error);
            }
        }
    }

    private async uploadImageSelection(media: LocalMediaRef): Promise<UploadFile> {
        const formData = new FormData();
        formData.append("file", media.file);
        formData.append("name", media.name || media.file.name);

        if (media?.caption) {
            formData.append("caption", media.caption);
        }

        const response = await fetch('/api/upload/strapi/upload', {
            method: 'POST',
            body: formData,
        });

        if (response.ok) {
            const data = await response.json();
            return data.body as UploadFile;
        } else {
            const data = await response.json();
            throw new StrapiMediaManagerError(`Image upload error: ${data.error}`);
        }
    }
}
